import { z } from 'zod/v3';

/**
 * RFC-179 canonical schema: IDENTITY_GROUP.
 *
 * Vendor-agnostic "a named collection of principals with members" — covers
 * AAD groups, Okta groups, GitHub teams, AWS IAM groups. The audit value is
 * cross-referencing: a SECRET_ACCESS_GRANT row points at a group principal
 * (`principalType: 'group'`); the auditor opens the IDENTITY_GROUP sibling
 * CSV and resolves the group's effective members by id.
 *
 * Membership is **direct only** in v1. Nested-group resolution requires
 * recursive Graph walks across providers with differing depth semantics —
 * the `hasNestedGroups: true` flag is the auditor's cue to follow up. The
 * `membersFetchedAt` field reminds reviewers that membership is a point-in-time
 * snapshot — changes after the scan won't reflect.
 */

export const IDENTITY_GROUP_SCHEMA_VERSION = 1;

const GroupMember = z.object({
  externalId: z.string(),
  type: z.enum(['user', 'group', 'service_account', 'unknown']),
  displayName: z.string().nullable(),
  /** Email when known (AAD/Okta users). Service accounts often have one too. */
  email: z.string().email().nullable(),
});

export const IdentityGroup = z.object({
  externalId: z.string(),
  /** Human-readable group name. Auditor's primary identifier when scanning the CSV. */
  displayName: z.string().nullable(),
  description: z.string().nullable(),
  /** Total direct member count — useful when the members array is truncated. */
  memberCount: z.number().int().min(0),
  /** Direct members only (nested groups appear as type='group' rows; expand separately). */
  members: z.array(GroupMember),
  /** True when any direct member is itself a group — auditor must follow up. */
  hasNestedGroups: z.boolean(),
  /** ISO timestamp the membership snapshot was captured; reminds the auditor this is point-in-time. */
  membersFetchedAt: z.string(),
});

export type IdentityGroup = z.infer<typeof IdentityGroup>;
export type IdentityGroupMember = z.infer<typeof GroupMember>;
